import { useMemo, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Icon, Popup, Button } from "semantic-ui-react";
import { SelectColumnFilter, Table, SubRowAsync, DefaultColumnFilter } from "../Table/Table";
import { flattenObject } from "services/utils";
import { formatDate } from "services/common";
import { ACTIVE_CAMPAIGN_STATUSES } from "constants/campaignStatuses";
import { getDeliveriesTableState } from "store/selectors";

function DeliveriesTable({ data, dispatch, isLazyLoading = false, t }) {
  const { filterValues, globalFilterValue, isRemovingFilters } = useSelector(
    getDeliveriesTableState
  );
  const hasFilters = useMemo(() => filterValues.filter(({ value }) => value !== ""), [
    filterValues,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      isRemovingFilters && dispatch.reactTableDeliveries.resetFilters();
    }, 100);
    return () => clearTimeout(timer);
  }, [isRemovingFilters]);

  return (
    <>
      <Button
        style={{
          position: "relative",
          bottom: "-2.6rem",
        }}
        className="bmo-purple"
        disabled={hasFilters?.length <= 0 && !globalFilterValue}
        basic
        onClick={() => {
          dispatch.reactTableDeliveries.setIsRemovingFilters();
        }}
      >
        <Icon name="remove" />
        {t("campaigns_page_clear_filters")}
      </Button>
      <DeliveriesList data={data} dispatch={dispatch} isLazyLoading={isLazyLoading} />
    </>
  );
}

function DeliveriesList(props) {
  const { data, dispatch, isLazyLoading } = props;
  const { t } = useTranslation("common");
  const { filterValues, globalFilterValue, isRemovingFilters } = useSelector(
    getDeliveriesTableState
  );
  const skipPageResetRef = useRef();
  const campaignsData = useMemo(() => {
    skipPageResetRef.current = true;
    const newData = data.map((obj, i) => flattenObject(obj));
    return newData;
  }, [data]);
  const campaignStatusOptions = useMemo(
    () =>
      ACTIVE_CAMPAIGN_STATUSES.map((status) => ({
        value: status,
        label: status.replace(/_/g, " "),
      })),
    []
  );

  const onFilterChange = (value, id) => {
    dispatch.reactTableDeliveries.addOrUpdateFilterValue({ value: value, id: id });
  };
  const setGlobalFilterValue = (value) => {
    dispatch.reactTableDeliveries.setGlobalFilterValue(value);
  };
  const columns = useMemo(
    () => [
      {
        Header: t("campaign_name"),
        accessor: "name",
        Cell: function NameRow({ row }) {
          return (
            <Popup
              content={row.original.name}
              position="top center"
              pinned
              trigger={<p style={{ width: "max-content" }}>{row.original.name}</p>}
            />
          );
        },
        SubCell: () => null,
        Filter: function filter(props) {
          return (
            <DefaultColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
        width: 220,
      },
      {
        Header: t("campaign_advertiser"),
        accessor: "advertiser",
        Cell: function CampaignRow({ row }) {
          return (
            <Popup
              content={row.original.advertiser}
              position="top center"
              pinned
              trigger={
                <p style={{ width: "max-content", textAlign: "center" }}>
                  {row.original.advertiser}
                </p>
              }
            />
          );
        },
        SubCell: () => null,
        Filter: function filter(props) {
          return (
            <DefaultColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
        width: 220,
      },
      {
        Header: t("campaign_type"),
        accessor: "type",
        Filter: function selectFilter(props) {
          return (
            <SelectColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
        SubCell: () => null,
      },
      {
        Header: t("campaign_display_assets"),
        accessor: "assetsLength",
        SubCell: () => null,
        disableFilters: true,
        width: 80,
      },
      {
        Header: t("campaign_contract_number"),
        accessor: "contract_number",
        width: 190,
        SubCell: () => null,
        Filter: function filter(props) {
          return (
            <DefaultColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
      },
      {
        Header: t("campaign_start_time"),
        accessor: "start_time",
        Cell: function dateCell({ row }) {
          return formatDate(row.original.start_time);
        },
        SubCell: () => null,
        Filter: function filter(props) {
          return (
            <DefaultColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
      },
      {
        Header: t("campaign_end_time"),
        accessor: "end_time",
        Cell: function dateCell({ row }) {
          return formatDate(row.original.end_time);
        },
        SubCell: () => null,
        Filter: function filter(props) {
          return (
            <DefaultColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
      },
      {
        Header: t("campaign_status"),
        accessor: "status",
        filter: "select",
        Cell: function CampaignStatusCell({ row }) {
          return row.original.status ? row.original.status.replace(/_/g, " ") : "-";
        },
        SubCell: () => null,
        Filter: function selectFilter(props) {
          return (
            <SelectColumnFilter
              {...props}
              t={t}
              options={campaignStatusOptions}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
        width: 200,
      },
      {
        // Make an expander cell
        Header: () => null, // No header
        id: "expander", // It needs an ID
        Cell: function rowExpanderCell({ row }) {
          // Use Cell to render an expander for each row.
          // We can use the getToggleRowExpandedProps prop-getter
          // to build the expander.
          return (
            <span
              {...row.getToggleRowExpandedProps()}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
              }}
              data-test={`expand_table_row_${row.original.name}`}
            >
              {row.isExpanded ? <Icon name="caret down" /> : <Icon name="caret right" />}
              {row.isExpanded ? (
                <span style={{ fontSize: "10px" }}>
                  {t("contributor_campaigns_view_close_row")}
                </span>
              ) : (
                <span style={{ fontSize: "10px" }}>{t("contributor_campaigns_view_open_row")}</span>
              )}
            </span>
          );
        },
        // We can override the cell renderer with a SubCell to be used with an expanded row
        SubCell: () => null, // No expander on an expanded row
      },
    ],
    [campaignStatusOptions, t]
  );

  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns, t }) => (
      <SubRowAsync
        row={row}
        rowProps={rowProps}
        visibleColumns={visibleColumns}
        renderAssets
        t={t}
      />
    ),
    []
  );

  return (
    <div className="page-container">
      <Table
        columns={columns}
        data={campaignsData}
        useTableGlobalFilter={true}
        t={t}
        isLazyLoading={isLazyLoading}
        tableClass="styled-table deliveries"
        renderRowSubComponent={renderRowSubComponent}
        defaultFilteredFields={filterValues}
        defaultGlobalFieldValue={globalFilterValue}
        setGlobalFilterValue={(value) => setGlobalFilterValue(value)}
        isRemovingFilters={isRemovingFilters}
        skipPageResetRef={skipPageResetRef}
      />
    </div>
  );
}

export default DeliveriesTable;
